import React, { CSSProperties, useState } from 'react';
import styled, { css } from 'styled-components';

import { Icon, Tooltip } from '@column/column-ui-kit';

import { copyToClipboard } from '~/util';

export interface ApiKeyPreviewStyleProps {
  isEmpty: boolean;
}

export interface ApiKeyPreviewProps extends Partial<ApiKeyPreviewStyleProps> {
  className?: string;
  value?: string;
  placeholder?: string;
  style?: CSSProperties;
}

const StyledApiKeyPreview = styled.div<ApiKeyPreviewStyleProps>`
  color: ${({ theme }) => theme.secondary.background};
  font-size: 12px;
  font-weight: 500;
  line-height: 24px;
  width: 132px;
  position: relative;

  ${({ isEmpty }) =>
    isEmpty &&
    css`
      color: ${({ theme }) => theme.secondary.blendToBackground(400)};
    `}
`;

const Value = styled.div`
  width: 100%;
  overflow: hidden;
  mask-image: linear-gradient(90deg, black 30%, transparent 80%);
`;

const StyledTooltip = styled(Tooltip)`
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;
`;

const CopyButton = styled.button`
  appearance: none;
  border: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
  margin: 0;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition:
    background-color 0.2s,
    color 0.2s,
    transform 0.2s,
    border-color 0.2s,
    box-shadow 0.2s;

  svg {
    --icon-color: currentColor;
    --icon-size: 16px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.secondary.blendToBackground(50)};
    border-color: ${({ theme }) => theme.secondary.blendToBackground(300)};
    color: ${({ theme }) => theme.secondary.blendToBackground(900)};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.95);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary.blendToBackground(350)};
    outline-offset: 1px;
  }
`;

export const ApiKeyPreview: React.FC<ApiKeyPreviewProps> = (props) => {
  const [isTooltipShow, setIsTooltipShow] = useState<boolean>(false);

  const styleProps: ApiKeyPreviewStyleProps = {
    isEmpty: !props.value,
  };

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    setIsTooltipShow(true);
    copyToClipboard(props.value ?? '');
    setTimeout(() => setIsTooltipShow(false), 2000);
  };

  return (
    <StyledApiKeyPreview style={props.style} className={props.className} {...styleProps}>
      <Value>{props.value || props.placeholder}</Value>
      <StyledTooltip content="Copied!" isOpen={isTooltipShow} offsetY={2}>
        <CopyButton onClick={handleCopyClick} type="button" aria-label="Copy API key">
          <Icon.Copy />
        </CopyButton>
      </StyledTooltip>
    </StyledApiKeyPreview>
  );
};
